import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  retry,
} from '@reduxjs/toolkit/query/react'
import { ENDPOINTS } from '@constants/index'
import { GuestActiveDataArgs, LinkCustomerArgs } from '@typesApp/CustomerApi'
import { AuthToken, AuthTokenArgs } from '@typesApp/Customer'
import { ActiveDataResult } from '@typesApp/ClaimRecord'
import { RetryOptions } from '@reduxjs/toolkit/dist/query/retry'
import { config } from '@config/config'
import { createGenericFetchError, getErrorMessage, isFetchBaseError } from '@utils/errorUtil'
import { authApi } from './auth'
import { unwrapMutationActionResult } from './unwrap'
import {
  copyFetchArgsWithoutEcommerceParam,
  GUEST_AUTH_HEADER_NAME,
  guestReauthController,
} from './reauth/guestReauthController'

const MAX_RETRY_COUNT = 5

const guestBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  // retry with backoff
  const retryOptions: RetryOptions = {
    retryCondition: (error, args, extraArgs): boolean => {
      return (
        // NOTE: status will be FETCH_ERROR if some local network conditions (like VPN) changes
        // and we do want to retry again in this case. No harm since we have a max retry count.
        (error.status === 409 || error.status === 500 || error.status === 504 || error.status === 'FETCH_ERROR') &&
        extraArgs.attempt <= MAX_RETRY_COUNT
      )
    },
  }

  const baseQuery = retry(
    fetchBaseQuery({
      baseUrl: config.getApiHost(),
    }),
    retryOptions
  )

  const argsForQuery = copyFetchArgsWithoutEcommerceParam(args)
  let result = await baseQuery(argsForQuery, api, extraOptions)

  // refetch guest token if required
  if (result?.error?.status === 401) {
    const service = {
      fetchAuthToken: async (tokenArgs: AuthTokenArgs) =>
        await unwrapMutationActionResult<AuthToken>(api.dispatch(authApi.endpoints.getAuthToken.initiate(tokenArgs))),
    }

    const controller = guestReauthController(service, args)

    try {
      // get updated fetch args with new token
      const updatedArgs = await controller.reauthOrThrow()
      result = await baseQuery(updatedArgs, api, extraOptions)
    } catch (error) {
      return handleError(error)
    }
  }

  return result
}

type ErrorReturn = {
  error: FetchBaseQueryError
}

const handleError = (error: unknown): ErrorReturn => {
  if (isFetchBaseError(error)) {
    return { error }
  }

  const message = getErrorMessage(error) ?? 'Reauth guest error'
  return { error: createGenericFetchError(message) }
}

// Note: all guest APIs require ecommerce param and guest auth headers

export const guestCustomerApi = createApi({
  reducerPath: 'guestCustomerApi',
  baseQuery: guestBaseQuery,
  tagTypes: [],
  endpoints: builder => ({
    getGuestActiveData: builder.query<ActiveDataResult, GuestActiveDataArgs>({
      query: ({ guestCustomerNumber, guestCustomerAccessToken, ecommerce }) => ({
        url: `${ENDPOINTS.DIRECT_BILLING.CLAIM_RECORDS}/active/${guestCustomerNumber}`,
        method: 'GET',
        params: {
          ecommerce,
        },
        headers: {
          [GUEST_AUTH_HEADER_NAME.CONTENT_TYPE]: 'application/json',
          [GUEST_AUTH_HEADER_NAME.AUTHORIZATION]: `Bearer ${guestCustomerAccessToken}`,
          [GUEST_AUTH_HEADER_NAME.CUSTOMER_NUMBER]: guestCustomerNumber,
        },
      }),
    }),

    linkCustomer: builder.mutation<void, LinkCustomerArgs>({
      query: ({ guestCustomerAccessToken, ...body }) => ({
        url: `${ENDPOINTS.DIRECT_BILLING.CUSTOMER}/link-customers`,
        method: 'POST',
        params: {
          ecommerce: body.ecommerce,
        },
        body,
        headers: {
          [GUEST_AUTH_HEADER_NAME.CONTENT_TYPE]: 'application/json',
          [GUEST_AUTH_HEADER_NAME.AUTHORIZATION]: `Bearer ${guestCustomerAccessToken}`,
          [GUEST_AUTH_HEADER_NAME.CUSTOMER_NUMBER]: body.guestCustomerNumber,
        },
      }),
      invalidatesTags: [],
    }),
  }),
})

export const { useLazyGetGuestActiveDataQuery, useLinkCustomerMutation } = guestCustomerApi
